import React, { useState, useMemo, useEffect } from 'react';
import { IProduct, IFlavor, IFlavorList, ISauce, ISauceList, ISideDish, ISideDishList, IAdditional, IAdditionalList, IIngredient, IIngredientList, IVariableDescription } from '../../types/Store';
import { useCart } from '../../contexts/CartContext';
import { useToast } from '../../contexts/ToastContext';
import { useStatus } from '../../contexts/StatusContext';
import { useStore } from '../../contexts/StoreContext';
import { useImageUrl } from '../../hooks/useImageUrl';
import * as S from './Styled';

export interface ProductModalProps {
  product: IProduct | null;
  isOpen: boolean;
  onClose: () => void;
  sauceOptions?: ISauce[];
  sauceLists?: ISauceList[];
  sideDishOptions?: ISideDish[];
  sideDishLists?: ISideDishList[];
  additionalOptions?: IAdditional[];
  additionalLists?: IAdditionalList[];
  flavorOptions?: IFlavor[];
  flavorLists?: IFlavorList[];
  ingredientOptions?: IIngredient[];
  ingredientLists?: IIngredientList[];
}

function CollapsibleSection({ label, children, closeWhen }: { label: React.ReactNode; children: React.ReactNode; closeWhen?: boolean }) {
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (!closeWhen) return;
    const t = setTimeout(() => setOpen(false), 280);
    return () => clearTimeout(t);
  }, [closeWhen]);

  return (
    <S.OptionsSection>
      <div
        role="button"
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          cursor: 'pointer', userSelect: 'none',
          padding: '10px 14px',
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '10px',
        }}
      >
        <S.OptionLabel style={{ margin: 0, flex: 1 }}>{label}</S.OptionLabel>
        <div style={{
          width: '28px', height: '28px', borderRadius: '50%',
          background: open ? 'var(--primary, #f58220)' : '#e2e8f0',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, transition: 'background 0.25s ease',
        }}>
          <span style={{
            display: 'inline-block',
            transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 0.25s ease',
            color: open ? 'white' : '#94a3b8',
            fontSize: '14px', lineHeight: 1,
          }}>▾</span>
        </div>
      </div>
      <div style={{
        display: 'grid',
        gridTemplateRows: open ? '1fr' : '0fr',
        transition: 'grid-template-rows 0.3s ease',
      }}>
        <div style={{ overflow: 'hidden', minHeight: 0, paddingTop: '8px' }}>
          {children}
        </div>
      </div>
    </S.OptionsSection>
  );
}

export function ProductModal({
  product,
  isOpen,
  onClose,
  sauceOptions = [],
  sauceLists = [],
  sideDishOptions = [],
  sideDishLists = [],
  additionalOptions = [],
  additionalLists = [],
  flavorOptions = [],
  flavorLists = [],
  ingredientOptions = [],
  ingredientLists = []
}: ProductModalProps) {
  const [selectedSauces, setSelectedSauces] = useState<ISauce[]>([]);
  const [sauceQtys, setSauceQtys] = useState<Record<string, number>>({});
  const [selectedSideDishes, setSelectedSideDishes] = useState<ISideDish[]>([]);
  const [additionalQtys, setAdditionalQtys] = useState<Record<string, number>>({});
  const [selectedSimpleAdditionals, setSelectedSimpleAdditionals] = useState<IAdditional[]>([]);
  const [includedAdditionalQtys, setIncludedAdditionalQtys] = useState<Record<string, number>>({});
  const [selectedFlavors, setSelectedFlavors] = useState<IFlavor[]>([]);
  const [removedIngredients, setRemovedIngredients] = useState<IIngredient[]>([]);
  const [selectedSize, setSelectedSize] = useState<any>(null);

  const [imageError, setImageError] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const { addItem } = useCart();
  const { showToast } = useToast();
  const { isOpen: storeIsOpen, isGestorMode } = useStatus();
  const { variableDescriptions } = useStore();
  const imageUrl = useImageUrl(product?.image);


  const getProductDescription = () => {
    if (!product) return '';
    if (product.useVariableDescription && product.variableDescriptionId) {
      const list = variableDescriptions.find((v: IVariableDescription) => (v._id || v.id) === product.variableDescriptionId);
      if (list) {
        const days = ['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'];
        const today = days[new Date().getDay()];
        // @ts-ignore
        const dayConfig = list.days[today];
        if (dayConfig && dayConfig.enabled && dayConfig.description) {
          return dayConfig.description;
        }
      }
    }
    return product.desc;
  };

  // --- FILTROS DE LISTA ---

  const filteredFlavors = useMemo(() => {
    if (!product?.hasFlavor) return [];
    const listId = (product as any).flavorListId?._id || product.flavorListId;
    if (listId) {
      const list = flavorLists.find(l => (String(l.id || l._id) === String(listId)));
      if (list && list.flavors && Array.isArray(list.flavors)) {
        return (list.flavors as any[])
          .filter((f: any) => typeof f !== 'object' || f.active !== false)
          .map((f: any) => (typeof f === 'object' ? { ...f, id: f.id || f._id } : { id: f, name: f, price: 0 }));
      }
      return [];
    }
    return flavorOptions.map((f: any) => ({ ...f, id: f.id || f._id }));
  }, [product, flavorOptions, flavorLists]);

  const filteredSauces = useMemo(() => {
    if (!product?.hasSauce) return [];
    const listId = (product as any).sauceListId?._id || product.sauceListId;
    if (listId) {
      const list = sauceLists.find(l => (String(l.id || l._id) === String(listId)));
      if (list && list.sauces && Array.isArray(list.sauces)) {
        return list.sauces.map((s: any) => (typeof s === 'object' ? { ...s, id: s.id || s._id } : { id: s, name: s, price: 0 }));
      }
      return [];
    }
    return sauceOptions.map((s: any) => ({ ...s, id: s.id || s._id }));
  }, [product, sauceOptions, sauceLists]);

  const filteredSideDishes = useMemo(() => {
    if (!product?.hasSideDish) return [];
    const listId = (product as any).sideDishListId?._id || product.sideDishListId;
    if (listId) {
      const list = sideDishLists.find(l => (String(l.id || l._id) === String(listId)));
      if (list && list.sideDishes && Array.isArray(list.sideDishes)) {
        return list.sideDishes.map((s: any) => (typeof s === 'object' ? { ...s, id: s.id || s._id } : { id: s, name: s, price: 0 }));
      }
      return [];
    }
    return sideDishOptions.map((s: any) => ({ ...s, id: s.id || s._id }));
  }, [product, sideDishOptions, sideDishLists]);

  const filteredAdditionals = useMemo(() => {
    if (!product?.hasAdditional) return [];
    const listId = (product as any).additionalListId?._id || product.additionalListId;
    if (listId) {
      const list = additionalLists.find(l => (String(l.id || l._id) === String(listId)));
      if (list && list.additionals && Array.isArray(list.additionals)) {
        return list.additionals.map((a: any) => (typeof a === 'object' ? { ...a, id: a.id || a._id } : { id: a, name: a, price: 0 }));
      }
      return [];
    }
    return additionalOptions.map((a: any) => ({ ...a, id: a.id || a._id }));
  }, [product, additionalOptions, additionalLists]);

  const filteredIncludedAdditionals = useMemo(() => {
    if (!(product as any)?.hasIncludedAdditionals) return [];
    const listId = (product as any).includedAdditionalListId?._id || (product as any).includedAdditionalListId;
    if (listId) {
      const list = additionalLists.find(l => (String(l.id || l._id) === String(listId)));
      if (list && list.additionals && Array.isArray(list.additionals)) {
        return list.additionals.map((a: any) => (typeof a === 'object' ? { ...a, id: a.id || a._id } : { id: a, name: a, price: 0 }));
      }
      return [];
    }
    return additionalOptions.map((a: any) => ({ ...a, id: a.id || a._id }));
  }, [product, additionalOptions, additionalLists]);

  const extrasChargeAdditionals = useMemo(() => {
    if (!product?.hasAdditional) return true;
    const listId = (product as any).additionalListId?._id || product.additionalListId;
    if (listId) {
      const list = additionalLists.find(l => String(l.id || l._id) === String(listId));
      return list?.chargeAdditionals !== false;
    }
    return true;
  }, [product, additionalLists]);

  const filteredIngredients = useMemo(() => {
    const canEdit = product?.hasIngredient || (product as any)?.allowCustomerEditIngredient;
    if (!canEdit) return [];

    if (product?.hasProductIngredients && product.ingredients && Array.isArray(product.ingredients) && product.ingredients.length > 0) {
      return ingredientOptions.filter(i => product.ingredients?.includes(String(i.id || i._id || '')));
    }

    const listId = (product as any)?.ingredientListId?._id || product?.ingredientListId;
    if (listId) {
      const list = ingredientLists.find(l => (String(l.id || l._id) === String(listId)));
      if (list && list.ingredients && Array.isArray(list.ingredients)) {
        return list.ingredients.map((i: any) => (typeof i === 'object' ? { ...i, id: i.id || i._id } : { id: i, name: i, active: true }));
      }
      return [];
    }

    return ingredientOptions.map((i: any) => ({ ...i, id: i.id || i._id }));
  }, [product, ingredientOptions, ingredientLists]);


  // --- PREÇO VARIÁVEL: menor preço da lista de acompanhamentos ---
  const variableMinPrice = useMemo(() => {
    if (!(product as any)?.hasVariablePrice || !product?.hasSideDish) return null;
    const listId = (product as any).sideDishListId?._id || product.sideDishListId;
    let items: any[] = [];
    if (listId) {
      const list = sideDishLists.find((l: any) => String(l.id || l._id) === String(listId));
      if (list?.sideDishes?.length) {
        items = list.sideDishes.map((s: any) =>
          typeof s === 'object' ? s : sideDishOptions.find((sd: any) => String(sd.id || sd._id) === String(s))
        ).filter(Boolean);
      }
    } else {
      items = sideDishOptions as any[];
    }
    const prices = items.filter((s: any) => (s.price || 0) > 0).map((s: any) => s.price as number);
    return prices.length > 0 ? Math.min(...prices) : null;
  }, [product, sideDishOptions, sideDishLists]);

  // Auto-seleciona quando há apenas uma opção obrigatória
  useEffect(() => {
    if (filteredFlavors.length === 1 && product?.hasFlavor) {
      setSelectedFlavors([filteredFlavors[0] as IFlavor]);
    }
  }, [filteredFlavors, product?.hasFlavor]);

  useEffect(() => {
    if (filteredSauces.length === 1 && product?.hasSauce && (product as any).sauceRequired !== false) {
      setSelectedSauces([filteredSauces[0] as ISauce]);
    }
  }, [filteredSauces, product?.hasSauce]);

  useEffect(() => {
    if (filteredSideDishes.length === 1 && product?.hasSideDish) {
      setSelectedSideDishes([filteredSideDishes[0] as ISideDish]);
    }
  }, [filteredSideDishes, product?.hasSideDish]);

  // --- DISPONIBILIDADE POR TURNO ---
  const isWithinSchedule = useMemo(() => {
    if (!(product as any)?.hasSpecificSchedule) return true;
    const schedule: any[] = (product as any).specificSchedule || [];
    if (schedule.length === 0) return true;
    const dayNames = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
    const now = new Date();
    const todayName = dayNames[now.getDay()];
    const todaySchedule = schedule.find((d: any) => d.day === todayName);
    if (!todaySchedule?.enabled) return false;
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    return todaySchedule.shifts.some((shift: any) => {
      const [oh, om] = shift.open.split(':').map(Number);
      const [ch, cm] = shift.close.split(':').map(Number);
      return currentMinutes >= oh * 60 + om && currentMinutes <= ch * 60 + cm;
    });
  }, [product]);

  const scheduleDescription = useMemo(() => {
    if (!(product as any)?.hasSpecificSchedule) return '';
    const schedule: any[] = (product as any).specificSchedule || [];
    const enabled = schedule.filter((d: any) => d.enabled);
    return enabled.map((d: any) => `${d.day}: ${d.shifts.map((s: any) => `${s.open}–${s.close}`).join(', ')}`).join('\n');
  }, [product]);

  // --- PREÇO TOTAL ---
  const productSizes: any[] = (product as any)?.hasVariableSizePrice
    ? ((product as any)?.sizes || []).filter((s: any) => s.active !== false)
    : [];

  const totalPrice = useMemo(() => {
    if (!product) return 0;
    if (product.isCombo) return product.price;
    if ((product as any).hasVariableSizePrice) {
      let total = selectedSize ? (selectedSize.price || 0) : 0;
      if (extrasChargeAdditionals) {
        if ((product as any).adicionalSimples) {
          selectedSimpleAdditionals.forEach(a => { total += (a.price || 0); });
        } else {
          Object.entries(additionalQtys).forEach(([name, qty]) => {
            const add = filteredAdditionals.find(a => a.name === name);
            if (add) total += (add.price || 0) * qty;
          });
        }
      }
      return total;
    }
    let total = (product as any).hasVariablePrice ? 0 : product.price;
    selectedFlavors.forEach(f => total += (f.price || 0));
    if ((product as any).sauceAllowMultiple) {
      filteredSauces.forEach((s: any) => { total += (s.price || 0) * (sauceQtys[s.name] || 0); });
    } else {
      selectedSauces.forEach(s => total += (s.price || 0));
    }
    selectedSideDishes.forEach(s => total += (s.price || 0));
    if (extrasChargeAdditionals) {
      if ((product as any).adicionalSimples) {
        selectedSimpleAdditionals.forEach(a => { total += (a.price || 0); });
      } else {
        Object.entries(additionalQtys).forEach(([name, qty]) => {
          const add = filteredAdditionals.find(a => a.name === name);
          if (add) total += (add.price || 0) * qty;
        });
      }
    }
    return total;
  }, [product, selectedFlavors, selectedSauces, sauceQtys, selectedSideDishes, additionalQtys, selectedSimpleAdditionals, filteredAdditionals, filteredSauces, selectedSize, extrasChargeAdditionals]);

  // --- HANDLERS ---

  const handleToggleItem = (item: any, selected: any[], setSelected: any, limit: number = 1) => {
    const isSelected = selected.some(i => (i.id || i._id) === (item.id || item._id));
    if (isSelected) {
      setSelected(selected.filter(i => (i.id || i._id) !== (item.id || item._id)));
    } else {
      if (limit === 1) {
        setSelected([item]);
      } else if (limit === 0 || selected.length < limit) {
        setSelected([...selected, item]);
      } else {
        showToast(`Limite máximo de ${limit} atingido`, 'warning');
      }
    }
  };

  const handleSauceQtyChange = (item: ISauce, delta: number) => {
    const name = (item as any).name as string;
    const current = sauceQtys[name] || 0;
    const limit = (product as any)?.sauceLimit || 0;
    if (delta > 0 && limit > 0) {
      const totalQty = Object.entries(sauceQtys).filter(([n]) => n !== name).reduce((s, [, q]) => s + q, 0) + current + delta;
      if (totalQty > limit) { showToast(`Limite de ${limit} molho(s) atingido`, 'warning'); return; }
    }
    const newQty = Math.max(0, current + delta);
    setSauceQtys(prev => ({ ...prev, [name]: newQty }));
    if (newQty > 0) {
      setSelectedSauces(prev => prev.some(s => s.name === name) ? prev : [...prev, item]);
    } else {
      setSelectedSauces(prev => prev.filter(s => s.name !== name));
    }
  };

  const handleToggleIngredient = (ingredient: IIngredient) => {
    const isRemoved = removedIngredients.some(i => (i.id || i._id) === (ingredient.id || ingredient._id));
    if (isRemoved) {
      setRemovedIngredients(removedIngredients.filter(i => (i.id || i._id) !== (ingredient.id || ingredient._id)));
    } else {
      setRemovedIngredients([...removedIngredients, ingredient]);
    }
  };


  useEffect(() => {
    setImageError(false);
    if (!isOpen) {
      setSelectedSauces([]);
      setSauceQtys({});
      setSelectedSideDishes([]);
      setAdditionalQtys({});
      setSelectedSimpleAdditionals([]);
      setIncludedAdditionalQtys({});
      setSelectedFlavors([]);
      setRemovedIngredients([]);
      setSelectedSize(null);
    }
  }, [product, isOpen]);

  useEffect(() => {
    if (!isOpen || !(product as any)?.hasVariablePrice || filteredSideDishes.length === 0) return;
    const cheapest = filteredSideDishes.reduce(
      (min, s) => (s.price || 0) < (min.price || 0) ? s : min,
      filteredSideDishes[0]
    );
    setSelectedSideDishes([cheapest]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, product?.id]);

  useEffect(() => {
    if (!isOpen || !(product as any)?.hasVariableSizePrice || productSizes.length === 0) return;
    const cheapest = productSizes.reduce(
      (min: any, s: any) => (s.price || 0) < (min.price || 0) ? s : min,
      productSizes[0]
    );
    setSelectedSize(cheapest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, product?.id]);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const canAddToCart = useMemo(() => {
    if (!product) return false;
    if (!isWithinSchedule) return false;
    if (product.isCombo) return true;
    if (product.hasFlavor && filteredFlavors.length > 0 && selectedFlavors.length === 0) return false;
    if (product.hasSauce && (product as any).sauceRequired !== false && (product.sauceLimit || 0) > 0 && filteredSauces.length > 0 && selectedSauces.length < ((product as any).sauceMinimum || 1)) return false;
    if (product.hasSideDish && !(product as any).hasVariablePrice && filteredSideDishes.length > 0 && selectedSideDishes.length === 0) return false;
    if ((product as any).hasIncludedAdditionals && (product as any).includedAdditionalRequired && filteredIncludedAdditionals.length > 0) {
      const total = Object.values(includedAdditionalQtys).reduce((s, q) => s + q, 0);
      if (total === 0) return false;
    }
    return true;
  }, [product, isWithinSchedule, selectedFlavors, selectedSauces, selectedSideDishes, filteredFlavors, filteredSauces, filteredSideDishes, filteredIncludedAdditionals, includedAdditionalQtys]);

  const handleAddToCart = () => {
    if (!product) return;

    if (!isWithinSchedule) {
      setShowScheduleModal(true);
      return;
    }

    if (product.isCombo) {
      addItem({ ...product }, 1);
      showToast('Produto adicionado!', 'success');
      handleClose();
      return;
    }

    if ((product as any).hasVariableSizePrice && productSizes.length > 0 && !selectedSize) {
        showToast('Por favor, selecione um tamanho', 'warning');
        return;
    }
    if (product.hasFlavor && filteredFlavors.length > 0 && selectedFlavors.length === 0) {
        showToast('Por favor, selecione pelo menos um sabor', 'warning');
        return;
    }
    if (product.hasSauce && (product as any).sauceRequired !== false && (product.sauceLimit || 0) > 0 && filteredSauces.length > 0) {
        const sauceMin = (product as any).sauceMinimum || 1;
        if (selectedSauces.length < sauceMin) {
            showToast(`Escolha ao menos ${sauceMin} molho${sauceMin > 1 ? 's' : ''}`, 'warning');
            return;
        }
    }
    if (product.hasSideDish && !(product as any).hasVariablePrice && filteredSideDishes.length > 0 && selectedSideDishes.length === 0) {
        const noun = (product as any).sideDishAsPrepMode ? 'um modo de preparo' : 'um acompanhamento';
        showToast(`Por favor, selecione ${noun}`, 'warning');
        return;
    }
    if ((product as any).hasIncludedAdditionals && (product as any).includedAdditionalRequired && filteredIncludedAdditionals.length > 0) {
        const total = Object.values(includedAdditionalQtys).reduce((s, q) => s + q, 0);
        if (total === 0) {
            showToast('Por favor, escolha os adicionais inclusos', 'warning');
            return;
        }
    }
    if (!canAddToCart) return;

    const options: any = {};
    if (selectedSize) options.size = selectedSize.name;
    if (selectedSauces.length > 0) {
      options.sauces = selectedSauces.map(i => i.name);
      if ((product as any).sauceAllowMultiple) {
        const qtys: Record<string, number> = {};
        selectedSauces.forEach(s => { if (sauceQtys[s.name] > 0) qtys[s.name] = sauceQtys[s.name]; });
        options.sauceQtys = qtys;
      }
    }
    if (selectedSideDishes.length > 0) options.sideDishes = selectedSideDishes.map(i => i.name);
    const includedAdditionalEntries = Object.entries(includedAdditionalQtys).filter(([, q]) => q > 0);
    if (includedAdditionalEntries.length > 0) {
      const expanded: string[] = [];
      includedAdditionalEntries.forEach(([name, qty]) => { for (let i = 0; i < qty; i++) expanded.push(name); });
      options.includedAdditionals = expanded;
    }
    if ((product as any).adicionalSimples) {
      if (selectedSimpleAdditionals.length > 0) {
        options.additionals = selectedSimpleAdditionals.map(a => a.name);
        options.additionalQtys = Object.fromEntries(selectedSimpleAdditionals.map(a => [a.name, 1]));
      }
    } else {
      const additionalEntries = Object.entries(additionalQtys).filter(([, q]) => q > 0);
      if (additionalEntries.length > 0) {
        const expanded: string[] = [];
        additionalEntries.forEach(([name, qty]) => { for (let i = 0; i < qty; i++) expanded.push(name); });
        options.additionals = expanded;
        options.additionalQtys = Object.fromEntries(additionalEntries);
      }
    }
    if (selectedFlavors.length > 0) options.flavors = selectedFlavors.map(i => i.name);
    if (removedIngredients.length > 0) options.removedIngredients = removedIngredients.map(i => i.name);

    addItem({ ...product, price: totalPrice }, 1, Object.keys(options).length > 0 ? options : undefined);
    showToast('Produto adicionado!', 'success');
    handleClose();
  };

  const handleAdditionalIncrement = (item: IAdditional) => {
    const limit = (product as any)?.additionalLimit || 0;
    if (limit > 0) {
      const total = Object.values(additionalQtys).reduce((s, q) => s + q, 0);
      if (total >= limit) { showToast(`Limite máximo de ${limit} adicional(ais) atingido`, 'warning'); return; }
    }
    setAdditionalQtys(prev => ({ ...prev, [item.name]: (prev[item.name] || 0) + 1 }));
  };

  const handleAdditionalDecrement = (item: IAdditional) => {
    const current = additionalQtys[item.name] || 0;
    if (current <= 1) {
      setAdditionalQtys(prev => { const n = { ...prev }; delete n[item.name]; return n; });
    } else {
      setAdditionalQtys(prev => ({ ...prev, [item.name]: prev[item.name] - 1 }));
    }
  };

  const handleIncludedIncrement = (item: IAdditional) => {
    const limit = (product as any)?.includedAdditionalLimit || 1;
    const total = Object.values(includedAdditionalQtys).reduce((s, q) => s + q, 0);
    if (total >= limit) { showToast(`Limite máximo de ${limit} adicional(ais) incluso(s) atingido`, 'warning'); return; }
    setIncludedAdditionalQtys(prev => ({ ...prev, [item.name]: (prev[item.name] || 0) + 1 }));
  };

  const handleIncludedDecrement = (item: IAdditional) => {
    const current = includedAdditionalQtys[item.name] || 0;
    if (current <= 1) {
      setIncludedAdditionalQtys(prev => { const n = { ...prev }; delete n[item.name]; return n; });
    } else {
      setIncludedAdditionalQtys(prev => ({ ...prev, [item.name]: prev[item.name] - 1 }));
    }
  };

  const handleClose = () => {
    setSelectedSauces([]);
    setSelectedSideDishes([]);
    setAdditionalQtys({});
    setSelectedSimpleAdditionals([]);
    setIncludedAdditionalQtys({});
    setSelectedFlavors([]);
    setRemovedIngredients([]);
    setShowScheduleModal(false);
    onClose();
  };

  if (!product || !isOpen) return null;

  const hasImage = !!(imageUrl && !imageError);

  return (
    <S.ModalWrapper onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <S.ModalContent $hasImage={hasImage}>
        <S.CloseButton onClick={handleClose}>&times;</S.CloseButton>
        {hasImage && (
          <S.ImageWrapper>
            <S.ProductImage src={imageUrl!} alt={product.name} onError={() => setImageError(true)} />
          </S.ImageWrapper>
        )}

        <S.ContentWrapper $fullWidth={!hasImage} style={!hasImage ? { paddingTop: '2.5rem' } : undefined}>
          <S.ProductInfo>
            <S.ProductName>{product.name}</S.ProductName>
            {getProductDescription() && <S.ProductDescription>{getProductDescription()}</S.ProductDescription>}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {(product as any).hasVariablePrice && selectedSideDishes.length === 0 && variableMinPrice !== null ? (
                <S.ProductPrice style={{ fontSize: '0.95rem', marginBottom: 0 }}>
                  a partir de {variableMinPrice.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </S.ProductPrice>
              ) : (
                <S.ProductPrice style={{ marginBottom: 0 }}>{totalPrice.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</S.ProductPrice>
              )}
              {(product as any).hasVariableSizePrice && selectedSize && (
                <div style={{ fontSize: '0.78rem', color: '#6b7280', marginLeft: '20px' }}>Tamanho: {selectedSize.name}</div>
              )}
            </div>
          </S.ProductInfo>

          <S.OptionsScrollArea>
          {/* COMBO: informational list — customization happens in the cart */}
          {product.isCombo && (product.comboItems || []).length > 0 && (
            <S.OptionsSection>
              <S.OptionLabel>Itens inclusos</S.OptionLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                {(product.comboItems || []).map(ci => (
                  <div key={ci.productId} style={{ fontSize: '0.88rem', color: '#374151' }}>
                    • {ci.productName}{ci.quantity > 1 ? ` (×${ci.quantity})` : ''}
                  </div>
                ))}
              </div>
              <p style={{ margin: '8px 0 0 0', fontSize: '0.78rem', color: '#94a3b8' }}>
                Personalize cada item no carrinho após adicionar.
              </p>
            </S.OptionsSection>
          )}

          {/* REGULAR PRODUCT OPTIONS (hidden when combo) */}
          {!product.isCombo && (
            <>
              {(product as any).hasVariableSizePrice && productSizes.length > 0 && (
                <S.OptionsSection>
                  <S.OptionLabel>Escolha o tamanho <span style={{color:'#ef4444', fontSize:'0.7rem'}}>* Obrigatório</span></S.OptionLabel>
                  <S.ChipsContainer>
                    {productSizes.map((size: any) => (
                      <S.Chip
                        key={size.name}
                        $active={selectedSize?.name === size.name}
                        onClick={() => setSelectedSize(size)}
                        type="button"
                        style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}
                      >
                        <span style={{ fontWeight: 700 }}>{size.name}</span>
                        {size.description && <span style={{ fontSize: '0.72rem', opacity: 0.8 }}>{size.description}</span>}
                        <span style={{ color: '#10b981', fontWeight: 700 }}>R$ {Number(size.price).toFixed(2)}</span>
                      </S.Chip>
                    ))}
                  </S.ChipsContainer>
                </S.OptionsSection>
              )}

              {product.hasFlavor && filteredFlavors.length > 0 && (
                <CollapsibleSection label={<>Escolha o(s) sabor(es) {product.flavorLimit && `(até ${product.flavorLimit})`} <span style={{color: '#ef4444', fontSize: '0.7rem'}}>* Obrigatório</span></>} closeWhen={selectedFlavors.length >= (product.flavorLimit || 1)}>
                  <S.OptionTable>
                    {filteredFlavors.map(item => {
                      const isActive = selectedFlavors.some(i => (i.id || i._id) === (item.id || item._id));
                      return (
                        <S.OptionRow key={item.id || item._id} $active={isActive} onClick={() => handleToggleItem(item, selectedFlavors, setSelectedFlavors, product.flavorLimit)} type="button">
                          <S.OptionCheckbox $active={isActive}>
                            {isActive && <span style={{color:'white', fontSize:'11px', fontWeight:700, lineHeight:1}}>✓</span>}
                          </S.OptionCheckbox>
                          <S.OptionRowInfo>
                            <S.OptionRowName $active={isActive}>{item.name}</S.OptionRowName>
                            {item.description && <S.OptionRowDesc>{item.description}</S.OptionRowDesc>}
                          </S.OptionRowInfo>
                          {item.price > 0 && <S.OptionRowPrice>+R$ {item.price.toFixed(2)}</S.OptionRowPrice>}
                        </S.OptionRow>
                      );
                    })}
                  </S.OptionTable>
                </CollapsibleSection>
              )}

              {product.hasSauce && filteredSauces.length > 0 && (() => {
                const allowMultiple = !!(product as any).sauceAllowMultiple;
                const totalQtySelected = allowMultiple
                  ? Object.values(sauceQtys).reduce((s, q) => s + q, 0)
                  : selectedSauces.length;
                return (
                  <CollapsibleSection label={(() => {
                      const sMin = (product as any).sauceMinimum || 1;
                      const sMax = product.sauceLimit || 0;
                      const sReq = (product as any).sauceRequired !== false;
                      if (sMax === 0) return 'Escolha o Molho (opcional)';
                      if (!sReq) return `Escolha até ${sMax} Molho${sMax > 1 ? 's' : ''}.`;
                      const label = sMin === sMax ? `Escolha ${sMin} Molho${sMin > 1 ? 's' : ''}` : `Escolha de ${sMin} a ${sMax} Molho${sMax > 1 ? 's' : ''}`;
                      return <>{label}{' '}<span style={{color: '#ef4444', fontSize: '0.7rem'}}>* Obrigatório</span></>;
                    })()} closeWhen={!allowMultiple && (product.sauceLimit || 0) > 0 && totalQtySelected >= (product.sauceLimit || 1)}>
                    <S.OptionTable>
                      {filteredSauces.map(item => {
                        if (allowMultiple) {
                          const qty = sauceQtys[item.name] || 0;
                          return (
                            <S.OptionRow key={item.id || item._id} $active={qty > 0} style={{cursor: 'default'}}>
                              <S.OptionRowInfo>
                                <S.OptionRowName $active={qty > 0}>{item.name}</S.OptionRowName>
                              </S.OptionRowInfo>
                              {item.price > 0 && <S.OptionRowPrice>+R$ {item.price.toFixed(2)}</S.OptionRowPrice>}
                              <div style={{display:'flex', alignItems:'center', gap:'6px', marginLeft:'auto'}}>
                                <button type="button" onClick={() => handleSauceQtyChange(item as ISauce, -1)} style={{width:26, height:26, borderRadius:'50%', border:'1px solid #cbd5e1', background: qty > 0 ? '#ef4444' : '#f1f5f9', color: qty > 0 ? '#fff' : '#94a3b8', fontWeight:700, fontSize:'16px', cursor:'pointer', lineHeight:1}}>−</button>
                                <span style={{minWidth:18, textAlign:'center', fontWeight:600, fontSize:'0.95rem'}}>{qty}</span>
                                <button type="button" onClick={() => handleSauceQtyChange(item as ISauce, 1)} style={{width:26, height:26, borderRadius:'50%', border:'1px solid #cbd5e1', background:'#22c55e', color:'#fff', fontWeight:700, fontSize:'16px', cursor:'pointer', lineHeight:1}}>+</button>
                              </div>
                            </S.OptionRow>
                          );
                        }
                        const isActive = selectedSauces.some(i => (i.id || i._id) === (item.id || item._id));
                        return (
                          <S.OptionRow key={item.id || item._id} $active={isActive} onClick={() => handleToggleItem(item, selectedSauces, setSelectedSauces, product.sauceLimit)} type="button">
                            <S.OptionCheckbox $active={isActive}>
                              {isActive && <span style={{color:'white', fontSize:'11px', fontWeight:700, lineHeight:1}}>✓</span>}
                            </S.OptionCheckbox>
                            <S.OptionRowInfo>
                              <S.OptionRowName $active={isActive}>{item.name}</S.OptionRowName>
                            </S.OptionRowInfo>
                            {item.price > 0 && <S.OptionRowPrice>+R$ {item.price.toFixed(2)}</S.OptionRowPrice>}
                          </S.OptionRow>
                        );
                      })}
                    </S.OptionTable>
                  </CollapsibleSection>
                );
              })()}

              {product.hasSideDish && filteredSideDishes.length > 0 && (
                <CollapsibleSection closeWhen={selectedSideDishes.length >= (product.sideDishLimit || 1)} label={<>
                    {(product as any).hasVariablePrice
                      ? ((product as any).sideDishAsPrepMode ? 'Modo de Preparo' : 'Acompanhamento')
                      : ((product as any).sideDishAsPrepMode
                          ? `Escolha o(s) modo(s) de preparo ${product.sideDishLimit ? `(até ${product.sideDishLimit})` : ''}`
                          : `Escolha o(s) acompanhamento(s) ${product.sideDishLimit ? `(até ${product.sideDishLimit})` : ''}`)
                    }
                    {!(product as any).hasVariablePrice && <span style={{color: '#ef4444', fontSize: '0.7rem'}}> * Obrigatório</span>}
                  </>}>
                  <S.OptionTable>
                    {filteredSideDishes.map(item => {
                      const isActive = selectedSideDishes.some(i => (i.id || i._id) === (item.id || item._id));
                      return (
                        <S.OptionRow key={item.id || item._id} $active={isActive} onClick={() => handleToggleItem(item, selectedSideDishes, setSelectedSideDishes, product.sideDishLimit)} type="button">
                          <S.OptionCheckbox $active={isActive}>
                            {isActive && <span style={{color:'white', fontSize:'11px', fontWeight:700, lineHeight:1}}>✓</span>}
                          </S.OptionCheckbox>
                          <S.OptionRowInfo>
                            <S.OptionRowName $active={isActive}>{item.name}</S.OptionRowName>
                          </S.OptionRowInfo>
                          {item.price > 0 && (
                            <S.OptionRowPrice>
                              {(product as any).hasVariablePrice ? '' : '+'}R$ {item.price.toFixed(2)}
                            </S.OptionRowPrice>
                          )}
                        </S.OptionRow>
                      );
                    })}
                  </S.OptionTable>
                </CollapsibleSection>
              )}

              {(product as any).hasIncludedAdditionals && filteredIncludedAdditionals.length > 0 && (
                <CollapsibleSection label={<>Adicionais Inclusos <span style={{color:'#059669', fontSize:'0.7rem', fontWeight:700}}>{(product as any).includedAdditionalRequired ? '* Obrigatório · Grátis' : 'Opcional · Grátis'}</span></>} closeWhen={Object.values(includedAdditionalQtys).reduce((s: number, q) => s + q, 0) >= ((product as any).includedAdditionalLimit || 1)}>
                  <S.HintText style={{marginBottom:'8px', color:'#059669'}}>
                    Escolha até {(product as any).includedAdditionalLimit || 1} adicional(ais) incluso(s) sem custo.
                  </S.HintText>
                  <S.OptionTable>
                    {filteredIncludedAdditionals.map((addItem: any) => {
                      const qty = includedAdditionalQtys[addItem.name] || 0;
                      return (
                        <div key={addItem.id || addItem._id} style={{display:'flex', alignItems:'center', gap:'10px', padding:'11px 12px', borderBottom:'1px dashed #e2e8f0', background: qty > 0 ? 'rgba(5,150,105,0.06)' : 'white'}}>
                          <S.OptionRowInfo>
                            <S.OptionRowName $active={qty > 0}>{addItem.name}</S.OptionRowName>
                            <S.OptionRowDesc style={{color:'#059669', fontWeight:600}}>Grátis</S.OptionRowDesc>
                          </S.OptionRowInfo>
                          {qty === 0 ? (
                            <button type="button" onClick={() => handleIncludedIncrement(addItem)} style={{padding:'5px 12px', borderRadius:'20px', background:'#f0fdf4', border:'1px solid #bbf7d0', cursor:'pointer', fontSize:'0.78rem', fontWeight:600, color:'#059669', flexShrink:0}}>+ Incluir</button>
                          ) : (
                            <div style={{display:'flex', alignItems:'center', gap:'8px', flexShrink:0}}>
                              <button type="button" onClick={() => handleIncludedDecrement(addItem)} style={{width:'26px', height:'26px', borderRadius:'50%', background:'#6b7280', color:'white', border:'none', cursor:'pointer', fontWeight:700, fontSize:'1rem', lineHeight:'1'}}>−</button>
                              <span style={{fontWeight:700, minWidth:'20px', textAlign:'center', fontSize:'0.95rem'}}>{qty}</span>
                              <button type="button" onClick={() => handleIncludedIncrement(addItem)} style={{width:'26px', height:'26px', borderRadius:'50%', background:'#059669', color:'white', border:'none', cursor:'pointer', fontWeight:700, fontSize:'1rem', lineHeight:'1'}}>+</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </S.OptionTable>
                </CollapsibleSection>
              )}

              {product.hasAdditional && filteredAdditionals.length > 0 && (
                (product as any).adicionalSimples ? (
                  <CollapsibleSection label={<>Adicionais <span style={{color: '#6b7280', fontSize: '0.7rem'}}>(Opcional{!extrasChargeAdditionals ? ' · Grátis' : ''})</span></>}>
                    <S.OptionTable>
                      {filteredAdditionals.map(addItem => {
                        const isActive = selectedSimpleAdditionals.some(a => (a.id || a._id) === (addItem.id || addItem._id));
                        return (
                          <S.OptionRow key={addItem.id || addItem._id} $active={isActive}
                            onClick={() => handleToggleItem(addItem, selectedSimpleAdditionals, setSelectedSimpleAdditionals, filteredAdditionals.length)}
                            type="button">
                            <S.OptionCheckbox $active={isActive}>
                              {isActive && <span style={{color:'white', fontSize:'11px', fontWeight:700, lineHeight:1}}>✓</span>}
                            </S.OptionCheckbox>
                            <S.OptionRowInfo>
                              <S.OptionRowName $active={isActive}>{addItem.name}</S.OptionRowName>
                              {extrasChargeAdditionals
                                ? addItem.price > 0 && <S.OptionRowDesc>+R$ {addItem.price.toFixed(2)}</S.OptionRowDesc>
                                : <S.OptionRowDesc style={{color:'#059669', fontWeight:600}}>Grátis</S.OptionRowDesc>
                              }
                            </S.OptionRowInfo>
                          </S.OptionRow>
                        );
                      })}
                    </S.OptionTable>
                  </CollapsibleSection>
                ) : (
                  <CollapsibleSection label={<>Adicionais Extras <span style={{color: '#6b7280', fontSize: '0.7rem'}}>(Opcional{!extrasChargeAdditionals ? ' · Grátis' : ''})</span></>} closeWhen={(product as any).additionalLimit > 0 && Object.values(additionalQtys).reduce((s: number, q) => s + q, 0) >= (product as any).additionalLimit}>
                    <S.OptionTable>
                      {filteredAdditionals.map(addItem => {
                        const qty = additionalQtys[addItem.name] || 0;
                        return (
                          <div key={addItem.id || addItem._id} style={{display:'flex', alignItems:'center', gap:'10px', padding:'11px 12px', borderBottom:'1px dashed #e2e8f0', background: qty > 0 ? 'rgba(245,130,32,0.06)' : 'white'}}>
                            <S.OptionRowInfo>
                              <S.OptionRowName $active={qty > 0}>{addItem.name}</S.OptionRowName>
                              {extrasChargeAdditionals
                                ? addItem.price > 0 && <S.OptionRowDesc>+R$ {addItem.price.toFixed(2)} cada</S.OptionRowDesc>
                                : <S.OptionRowDesc style={{color:'#059669', fontWeight:600}}>Grátis</S.OptionRowDesc>
                              }
                            </S.OptionRowInfo>
                            {qty === 0 ? (
                              <button type="button" onClick={() => handleAdditionalIncrement(addItem)} style={{padding:'5px 12px', borderRadius:'20px', background:'#f1f5f9', border:'1px solid #e2e8f0', cursor:'pointer', fontSize:'0.78rem', fontWeight:600, color:'#374151', flexShrink:0}}>+ Adicionar</button>
                            ) : (
                              <div style={{display:'flex', alignItems:'center', gap:'8px', flexShrink:0}}>
                                <button type="button" onClick={() => handleAdditionalDecrement(addItem)} style={{width:'26px', height:'26px', borderRadius:'50%', background:'#ea580c', color:'white', border:'none', cursor:'pointer', fontWeight:700, fontSize:'1rem', lineHeight:'1'}}>−</button>
                                <span style={{fontWeight:700, minWidth:'20px', textAlign:'center', fontSize:'0.95rem'}}>{qty}</span>
                                <button type="button" onClick={() => handleAdditionalIncrement(addItem)} style={{width:'26px', height:'26px', borderRadius:'50%', background:'#10b981', color:'white', border:'none', cursor:'pointer', fontWeight:700, fontSize:'1rem', lineHeight:'1'}}>+</button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </S.OptionTable>
                  </CollapsibleSection>
                )
              )}

              {product.hasIngredient && filteredIngredients.length > 0 && (
                <CollapsibleSection label={<>Retirar Ingredientes (Opcional)</>}>
                  <S.OptionTable>
                    {filteredIngredients.map(item => {
                      const isActive = removedIngredients.some(i => (i.id || i._id) === (item.id || item._id));
                      return (
                        <S.OptionRow key={item.id || item._id} $active={isActive} onClick={() => handleToggleIngredient(item)} type="button">
                          <S.OptionCheckbox $active={isActive}>
                            {isActive && <span style={{color:'white', fontSize:'11px', fontWeight:700, lineHeight:1}}>✓</span>}
                          </S.OptionCheckbox>
                          <S.OptionRowInfo>
                            <S.OptionRowName $active={isActive}>{isActive ? `Sem ${item.name}` : item.name}</S.OptionRowName>
                          </S.OptionRowInfo>
                        </S.OptionRow>
                      );
                    })}
                  </S.OptionTable>
                </CollapsibleSection>
              )}
            </>
          )}

          </S.OptionsScrollArea>

          {!isWithinSchedule ? (
            <button
              onClick={() => setShowScheduleModal(true)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: '10px', background: '#e5e7eb', color: '#6b7280',
                fontWeight: 700, fontSize: '0.95rem', padding: '16px 32px',
                borderRadius: '16px', border: 'none', cursor: 'pointer',
                textTransform: 'uppercase',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              Produto não disponível no horário · ver horários
            </button>
          ) : (
            <S.AddButton
              onClick={handleAddToCart}
              disabled={(!storeIsOpen && !isGestorMode) || !canAddToCart}
              $storeOpen={storeIsOpen || isGestorMode}
            >
              {(!storeIsOpen && !isGestorMode) ? 'Loja Fechada' : 'Adicionar ao Carrinho'}
            </S.AddButton>
          )}
        </S.ContentWrapper>
      </S.ModalContent>

      {showScheduleModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          onClick={() => setShowScheduleModal(false)}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '24px', maxWidth: '340px', width: '100%' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#5b21b6', marginBottom: '8px' }}>Produto Indisponível</div>
            <p style={{ fontSize: '0.9rem', color: '#374151', marginBottom: '16px', lineHeight: 1.5 }}>
              Este produto está disponível apenas nos seguintes horários:
            </p>
            <div style={{ background: '#f5f3ff', borderRadius: '10px', padding: '12px', marginBottom: '20px' }}>
              {scheduleDescription.split('\n').map((line, i) => (
                <div key={i} style={{ fontSize: '0.85rem', color: '#5b21b6', fontWeight: 600, lineHeight: 1.8 }}>{line}</div>
              ))}
            </div>
            <button onClick={() => setShowScheduleModal(false)}
              style={{ width: '100%', padding: '12px', borderRadius: '10px', background: '#8b5cf6', color: 'white', border: 'none', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer' }}>
              Entendido
            </button>
          </div>
        </div>
      )}
    </S.ModalWrapper>
  );
}
